<template>
    <div class="mt-5 rounded-[6px] border border-gray-200 bg-white p-6 shadow-md">
        <div class="flex flex-col gap-1 border-b border-gray-200 pb-4">
            <div class="text-lg font-medium text-gray-800">Search metadata</div>
            <div class="text-sm text-gray-500">
                What premade template search matches against, next to the
                name, description, category and keywords. Never shown to users.
            </div>
            <div class="text-sm text-gray-600">{{ status }}</div>
            <div
                v-if="searchMetadata?.last_error"
                class="text-sm text-red-600"
            >
                Last generation failed: {{ searchMetadata.last_error }}
            </div>
            <div v-if="errors.search_metadata" class="text-sm text-red-600">
                {{ errors.search_metadata }}
            </div>
        </div>

        <div class="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div class="flex flex-col gap-6">
                <TextAreaInput
                    v-model="form.summary"
                    label="Summary"
                    :rows="4"
                    :error="errors.summary"
                />
                <MultipleSelect
                    v-model:value="selectedUseCases"
                    label="Use cases"
                    placeholder="Select use cases"
                    :options="options.use_cases"
                />
                <MultipleSelect
                    v-model:value="selectedIndustries"
                    label="Industries"
                    placeholder="Select industries"
                    :options="options.industries"
                />
                <MultipleSelect
                    v-model:value="selectedOccasions"
                    label="Occasions"
                    placeholder="Select occasions"
                    :options="options.occasions"
                />
            </div>
            <div class="flex flex-col gap-6">
                <TextAreaInput
                    v-model="form.styles"
                    label="Styles (comma separated)"
                    :rows="2"
                    :error="errors.styles"
                />
                <TextAreaInput
                    v-model="form.colors"
                    label="Colours (comma separated)"
                    :rows="2"
                    :error="errors.colors"
                />
                <TextAreaInput
                    v-model="form.search_terms"
                    label="Search terms (comma separated)"
                    :rows="6"
                    :error="errors.search_terms"
                />
            </div>
        </div>

        <div class="mt-5 flex justify-end gap-2">
            <AtomicButton
                size="md"
                color="purple"
                :disabled="loading"
                @click="regenerate"
            >
                Regenerate
            </AtomicButton>
            <AtomicButton
                size="md"
                color="yellow"
                :disabled="loading || form.summary.trim() === ''"
                @click="save"
            >
                Save and lock
            </AtomicButton>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { route } from 'ziggy-js';
import { router } from '@inertiajs/vue3';
import AtomicButton from '@atomic/Buttons/AtomicButton.vue';
import TextAreaInput from '@atomic/Inputs/TextAreaInput.vue';
import MultipleSelect from '@/components/Atomic/Inputs/MultipleSelect.vue';
import { create } from '@composables/useNotifications';
import useConfirm from '@/composables/useConfirm';
import IPremadeTemplateSearchMetadata, {
    IPremadeTemplateSearchMetadataOption,
    IPremadeTemplateSearchMetadataOptions,
} from '@/types/Templates/IPremadeTemplateSearchMetadata';

type IOption = IPremadeTemplateSearchMetadataOption;

const props = defineProps<{
    premadeTemplateId: number;
    searchMetadata: IPremadeTemplateSearchMetadata | null;
    options: IPremadeTemplateSearchMetadataOptions;
}>();

const pick = (options: IOption[], values: string[] | undefined): IOption[] =>
    options.filter((option) => values?.includes(option.id));

const form = ref({
    summary: props.searchMetadata?.summary ?? '',
    styles: (props.searchMetadata?.styles ?? []).join(', '),
    colors: (props.searchMetadata?.colors ?? []).join(', '),
    search_terms: (props.searchMetadata?.search_terms ?? []).join(', '),
});
const selectedUseCases = ref<IOption[]>(
    pick(props.options.use_cases, props.searchMetadata?.use_cases)
);
const selectedIndustries = ref<IOption[]>(
    pick(props.options.industries, props.searchMetadata?.industries)
);
const selectedOccasions = ref<IOption[]>(
    pick(props.options.occasions, props.searchMetadata?.occasions)
);
const errors = ref<Record<string, string>>({});
const loading = ref(false);
const { show } = useConfirm();

const status = computed((): string => {
    const metadata = props.searchMetadata;

    if (!metadata?.generated_at) {
        return 'Not generated yet. It is generated after the next screenshot, or with Regenerate.';
    }

    const date = new Date(metadata.generated_at).toLocaleString();

    return metadata.locked
        ? `Edited by hand on ${date} - locked, template changes won't regenerate it.`
        : `Generated by ${metadata.model ?? 'the LLM'} on ${date}.`;
});

const splitList = (value: string): string[] =>
    value
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item !== '');

const save = () => {
    loading.value = true;

    router.put(
        route('kiosk.premade-templates.search-metadata.update', {
            premadeTemplate: props.premadeTemplateId,
        }),
        {
            summary: form.value.summary,
            use_cases: selectedUseCases.value.map((option) => option.id),
            industries: selectedIndustries.value.map((option) => option.id),
            occasions: selectedOccasions.value.map((option) => option.id),
            styles: splitList(form.value.styles),
            colors: splitList(form.value.colors),
            search_terms: splitList(form.value.search_terms),
        },
        {
            preserveScroll: true,
            onSuccess: () => {
                errors.value = {};
                create({ title: 'Search metadata saved', type: 'success' });
            },
            onError: (err) => {
                errors.value = err;
                create({ title: 'Search metadata not saved', type: 'error' });
            },
            onFinish: () => {
                loading.value = false;
            },
        }
    );
};

const regenerate = async () => {
    if (props.searchMetadata?.locked) {
        const confirmed = await show({
            header: 'Regenerate search metadata',
            message:
                'This metadata was edited by hand. Regenerating replaces those edits.',
            type: 'danger',
            confirm: 'Regenerate',
        });

        if (!confirmed) {
            return;
        }
    }

    loading.value = true;

    router.post(
        route('kiosk.premade-templates.search-metadata.regenerate.store', {
            premadeTemplate: props.premadeTemplateId,
        }),
        {},
        {
            preserveScroll: true,
            onSuccess: () => {
                errors.value = {};
                create({
                    title: 'Regeneration queued - reload in a minute',
                    type: 'info',
                });
            },
            onError: (err) => {
                errors.value = err;
            },
            onFinish: () => {
                loading.value = false;
            },
        }
    );
};
</script>
